import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { missileImpactMs, missileFlightTotalMs, MISSILE_SURPLUS_STAGGER_MS, REPLAY_LEAD_MS } from '../rules/gameConfig'
import { buildOppReplaySteps } from '../rules/gameLogic'
import { cloneHomefront } from '../rules/subplotGrid'
import { addBrood } from '../rules/subplotBases'
import { TUTORIAL_V2_MACHINE } from '../config/tutorialV2Steps'

// ─── useTutorial ──────────────────────────────────────────────────────────────
// Holds a scripted tutorial's step machine (config/tutorialV2Steps'
// makeTutorialMachine: v2's, or v3's in config/tutorialV3Steps) and turns it into
// BattleScreen props. The machine decides what the player may do and resolves
// each round through the real rules; this hook only shows it, and plays each
// round's reveal with the live game's timing (useGameState.handleReveal): the
// enemy's round replays on their half, then the clashes. Less the recon planes
// and the replay's brood beat, which the tutorial's enemy never uses.

const NO_CLASH = {
    id: null, context: null, winner: null, selfStrength: null, oppStrength: null,
    oppTargetIndex: null, selfTargetIndex: null, absorbed: 0, aimS: null, result: null,
}
const REVEAL_LEAD_MS = 400
const RESULT_HOLD_MS = 1680

// A Grid with this round's declared broods stamped on, as the reveal opens.
function withBuilds(hf, builds, round) {
    if (!builds?.length) return hf
    const g = cloneHomefront(hf)
    for (const b of builds) addBrood(g, b.col, b.centerS, round)
    return g
}

// The enemy's round, step by step on their half (their moves, then their new
// drones), as a live war replays it before the clashes; null if the rules
// can't explain their board (then it just appears).
function oppReplaySteps({ submitted, before }) {
    return buildOppReplaySteps(before.p2.board, submitted.p2.board, submitted.p2.actions)
}

// What the reveal shows as it opens: the round as submitted, the enemy's half
// as it stood before their replay.
function revealStart(reveal) {
    const { submitted, before } = reveal
    return {
        selfBoard: submitted.p1.board,
        oppBoard: oppReplaySteps(reveal) ? before.p2.board : submitted.p2.board,
        selfHf: withBuilds(submitted.p1.homefront, submitted.p1.baseBuilds, before.round),
        oppHf: withBuilds(submitted.p2.homefront, submitted.p2.baseBuilds, before.round),
        selfLost: before.p1.structuresLost,
        oppLost: before.p2.structuresLost,
        yield: before.p1.yield,
        targetPlots: submitted.p1.targetPlots,
        targetSubplots: submitted.p1.targetSubplots,
    }
}

export default function useTutorialV2() {
    return useTutorial(TUTORIAL_V2_MACHINE)
}

// `machine` must be a module constant (it isn't a dependency of anything here).
// `startAt`: a step id to open on, already played up to (for reviewing it).
export function useTutorial(machine, { startAt = null } = {}) {
    const { initialState, reduce, currentStep, stepText, canSubmit, owedDropZones, targets } = machine
    const [state, setState] = useState(() => (startAt ? machine.startAt(startAt) : initialState()))
    const dispatch = useCallback((event) => setState(s => reduce(s, event)), [reduce])

    // What the reveal shows while it plays; null between reveals.
    const [anim, setAnim] = useState(null)
    const [clash, setClash] = useState(NO_CLASH)
    const [resupplyDeliveries, setResupplyDeliveries] = useState(null)
    const playingRef = useRef(null)
    const timersRef = useRef([])

    useEffect(() => () => {
        playingRef.current = null
        timersRef.current.forEach(clearTimeout)
    }, [])

    const playReveal = useCallback(async (reveal) => {
        const token = {}
        playingRef.current = token
        const alive = () => playingRef.current === token
        const delay = (ms) => new Promise(res => { timersRef.current.push(setTimeout(res, ms)) })
        const later = (ms, fn) => timersRef.current.push(setTimeout(() => { if (alive()) fn() }, ms))

        const { result, submitted } = reveal
        setResupplyDeliveries({
            self: result.detail.p1.delivery.delivered, opp: result.detail.p2.delivery.delivered,
            sources: { self: result.detail.p1.delivery.sources, opp: result.detail.p2.delivery.sources },
        })
        setAnim(revealStart(reveal))
        const replay = oppReplaySteps(reveal)
        if (replay) {
            await delay(REPLAY_LEAD_MS)
            for (const step of replay) {
                if (!alive()) return
                setAnim(a => ({ ...a, oppBoard: step.board }))
                await delay(step.holdMs)
            }
        }
        await delay(REVEAL_LEAD_MS)

        const targetPlots = submitted.p1.targetPlots
        for (let col = 0; col < result.events.length; col++) {
            const event = result.events[col]
            if (!alive()) return
            if (!event || event.type === 'NO_BATTLE') continue
            const { battlefieldId, type } = event
            // The corridor fights with the brood ledger it had when its turn came.
            const applyLedger = (board, ledger) =>
                (board && ledger) ? board.map(bf => (bf.id === battlefieldId ? { ...bf, ...ledger } : bf)) : board
            setAnim(a => ({ ...a, selfBoard: applyLedger(a.selfBoard, event.selfLedgerAtClash), oppBoard: applyLedger(a.oppBoard, event.oppLedgerAtClash) }))
            setClash({
                id: battlefieldId,
                context: event.context,
                winner: type === 'WIN' ? 'self' : type === 'LOSS' ? 'opp' : 'tie',
                selfStrength: event.selfStrength,
                oppStrength: event.oppStrength,
                oppTargetIndex: type === 'LOSS' ? (event.oppTargetIndex ?? event.retaliationRow ?? null) : null,
                selfTargetIndex: type === 'WIN' ? ((targetPlots[col] ?? null) !== null ? targetPlots[col] : (event.retaliationRow ?? null)) : null,
                absorbed: event.absorbed ?? 0,
                aimS: event.aimS ?? null,
                result: null,
            })
            const struckStructure = (event.destroyedStructures?.length ?? 0) > 0
            const winnerHasTarget =
                (type === 'WIN'  && ((targetPlots[col] ?? null) !== null || (event.retaliationRow ?? null) !== null)) ||
                (type === 'LOSS' && ((event.oppTargetIndex ?? null) !== null || (event.retaliationRow ?? null) !== null))
            if (type !== 'TIE' && (event.postClashOppHf || event.postClashSelfHf)) {
                // The crater grows one ring per landing shot, then the
                // post-clash Grid lands with the last one.
                const pairCount = Math.min(event.selfStrength ?? 0, event.oppStrength ?? 0)
                const arrival = winnerHasTarget ? missileImpactMs(pairCount, event.absorbed ?? 0) : 900
                const side = event.postClashOppHf ? 'oppHf' : 'selfHf'
                const stages = event.craterStages ?? []
                stages.forEach((hf, i) => later(arrival + i * MISSILE_SURPLUS_STAGGER_MS, () => setAnim(a => ({ ...a, [side]: hf }))))
                later(arrival + stages.length * MISSILE_SURPLUS_STAGGER_MS,
                    () => setAnim(a => ({ ...a, [side]: event.postClashOppHf ?? event.postClashSelfHf })))
            }
            const lastMissile = missileFlightTotalMs(event.selfStrength, event.oppStrength, winnerHasTarget) + 450
            await delay(Math.max(struckStructure ? 3000 : 1800, lastMissile))
            if (!alive()) return
            setClash(c => ({ ...c, result: event.washedByRockets ? 'TIE' : type }))
            await delay(RESULT_HOLD_MS)
            if (!alive()) return
            const clear = (board, extra) => board.map(bf => (bf.id !== battlefieldId ? bf : { ...bf, armyQty: 0, fortCount: 0, fortInConstruction: 0, ...extra }))
            setAnim(a => ({
                ...a,
                selfBoard: clear(a.selfBoard, { newArmies: 0, deliveredNewArmies: 0, movedInArmies: 0, isAttacking: false }),
                oppBoard: clear(a.oppBoard, {}),
            }))
            setClash(NO_CLASH)
        }
        if (!alive()) return
        playingRef.current = null
        setAnim(null)
        dispatch({ type: 'revealed' })
    }, [dispatch])

    useEffect(() => {
        if (state.reveal && !playingRef.current) playReveal(state.reveal)
    }, [state.reveal, playReveal])

    // A dead structure's silhouette landed on a kill bar mid-reveal.
    const onStructureKilled = useCallback((side) => {
        setAnim(a => (a ? { ...a, [side === 'opp' ? 'oppLost' : 'selfLost']: a[side === 'opp' ? 'oppLost' : 'selfLost'] + 1 } : a))
    }, [])

    // The machine moves on to the next round at submit, but the reveal only
    // starts (setAnim) on the next commit: until it does, show the round as
    // submitted, or the board flashes the next round's state for a frame (the
    // crosshairs drop their aims and snap back in).
    const pending = useMemo(() => (state.reveal ? revealStart(state.reveal) : null), [state.reveal])
    const shown = anim ?? pending

    const step = currentStep(state)
    const animating = !!anim || !!state.reveal
    const owed = owedDropZones(state)
    const planningOn = !animating && step.kind === 'act'

    return {
        step,
        text: stepText(state),
        tip: state.tip,
        canGoBack: !animating && state.history.length > 0,
        dispatch,

        // ── BattleScreen ──
        currentRound: step.round,
        selfBoard: shown?.selfBoard ?? state.board,
        oppBoard: shown?.oppBoard ?? state.war.p2.board,
        selfHf: shown?.selfHf ?? state.war.p1.homefront,
        oppHf: shown?.oppHf ?? state.war.p2.homefront,
        selfLost: shown?.selfLost ?? state.war.p1.structuresLost,
        oppLost: shown?.oppLost ?? state.war.p2.structuresLost,
        yieldBalance: shown?.yield ?? state.war.p1.yield,
        targetPlots: shown?.targetPlots ?? state.targetPlots,
        targetSubplots: shown?.targetSubplots ?? state.targetSubplots,
        armiesToDeploy: state.armiesToDeploy,
        canDeploy: planningOn && !!step.deploy && state.armiesToDeploy > 0,
        dropZones: state.dropZones,
        requiredDropZoneCount: owed,
        placingDropZones: planningOn && !!step.dropZone && state.dropZones.length < owed,
        isSubmitDisabled: animating || !canSubmit(state),
        aimArrow: planningOn && step.aims ? targets[step.aims] : null,
        isAnimating: !!shown,
        activeClashId: clash.id,
        clashResult: clash.result,
        activeClashContext: clash.context,
        activeClashWinner: clash.winner,
        activeClashSelfStrength: clash.selfStrength,
        activeClashOppStrength: clash.oppStrength,
        activeClashOppTargetIndex: clash.oppTargetIndex,
        activeClashSelfTargetIndex: clash.selfTargetIndex,
        activeClashAbsorbed: clash.absorbed,
        activeClashAimS: clash.aimS,
        resupplyDeliveries,
        onStructureKilled,
        onAction: (bfId, action, extras) => dispatch({ type: 'action', bfId, action, extras }),
        onTargetPlot: (col, subS) => dispatch({ type: 'aim', col, subS }),
        onDropZone: (col, action) => dispatch({ type: 'dropZone', col, action }),
        onSubmit: () => dispatch({ type: 'submit' }),
        onPeekChange: (held) => { if (held) dispatch({ type: 'peek' }) },
    }
}
